import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import ActionBar from "../../../Components/Button/ActionBar";
import SeatPlanForm from "../../../Components/Forms/SeatPlanForm";
import Delete from "../../../Components/Button/Delete";
import { toast } from "sonner";

interface Item {
  ticket_id: number;
  ticket_group_code: string;
  // Add more properties as per your item structure
}

export default function UpdateSeatPlan() {
  const location = useLocation();
  const id = location.state && location.state.id; // Get the ID of the seat plan to update
  const [image, setImage] = useState<(File | string)[]>([]); // Support both new files and existing image URLs
  const [ticket_group_code, setTicketGroupCode] = useState("");
  const [items, setItems] = useState<Item[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string>("");
  const isMounted = useRef(false);

  const navigate = useNavigate();

  // Fetch the role from localStorage
  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    setRole(storedRole);
  }, []);

  // Fetch the existing seat plan details
  useEffect(() => {
    isMounted.current = true;

    const fetchSeatPlan = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
          alert("Authentication token not found!");
          return;
        }

        const response = await axios.get(
          `https://cdn.pulp.ph/api/v1/pulpliveworld/seatPlan/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (isMounted.current) {
          const { ticket_group_code, images } = response.data;
          setTicketGroupCode(ticket_group_code);
          setImage(images || []); // Assuming `images` is an array of file URLs
        }
      } catch (error) {
        console.error("Error fetching seat plan details:", error);
      }
    };

    fetchSeatPlan();

    return () => {
      isMounted.current = false;
    };
  }, [id]);

  // Fetch ticket group codes for the dropdown
  useEffect(() => {
    const fetchTicketGroups = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        if (!authToken) {
          alert("Authentication token not found!");
          return;
        }

        const response = await axios.get(
          "https://cdn.pulp.ph/api/v1/pulpliveworld/ticket",
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (isMounted.current) {
          setItems(response.data.ticket);
        }
      } catch (error) {
        console.error("Error fetching ticket group codes:", error);
      }
    };

    fetchTicketGroups();
  }, []);

  const handleChangeTicketGroupCode = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setTicketGroupCode(e.target.value);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setImage((prevImages) => [...prevImages, ...selectedFiles]);
    }
  };

  const handleDeleteImage = (index: number) => {
    setImage((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("ticket_group_code", ticket_group_code);
    if (image.length > 0) {
      image.forEach((image, index) => {
        formData.append(`image[${index}]`, image);
      });
    }

    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        alert("Authentication token not found!");
        setIsSubmitting(false);
        return;
      }

      // Update the existing seat plan
      await axios.post(
        `https://cdn.pulp.ph/api/v1/pulpliveworld/seatPlan/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      toast.success(<p className="text-[16px]">Content was submitted successfully!</p>, {
        duration: 1500,
      });
      
      setTimeout(() => {
        navigate("/view/post/seat-plan");
      }, 1600);
    } catch (error) {
      console.error("There was an error updating the seat plan!", error);
      alert("Failed to update the seat plan. Please try again.");
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 3000);
    }
  };

  const handleDelete = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        alert("Authentication token not found!");
        return;
      }

      const confirmDelete = window.confirm(
        "Are you sure you want to delete this seat plan?"
      );
      if (!confirmDelete) {
        return;
      }

      await axios.delete(
        `https://cdn.pulp.ph/api/v1/pulpliveworld/seatPlan/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      alert("Seat Plan deleted successfully!");
      navigate("/view/post/seat-plan");
    } catch (error) {
      console.error("There was an error deleting the seat plan!", error);
      alert("Failed to delete the seat plan. Please try again.");
    }
  };

  return (
    <>
      <div className="w-full p-5">
        <div className="flex justify-between items-center mb-4">
          <h1 className="GraphikSemibold text-[20px] font-semibold">Edit Seat Plan</h1>
          <ActionBar
            role={role}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isSaved={isSubmitting}
          />
        </div>

        <div className="py-5 px-4 rounded bg-white">
          <SeatPlanForm
            image={image.filter((img): img is File => typeof img !== "string")}
            items={items}
            ticket_group_code={ticket_group_code}
            warningMessage={warningMessage}
            onTicketGroupCodeChange={handleChangeTicketGroupCode}
            onImageChange={handleImageChange}
            // onDeleteExistingImage={handleDeleteImage}
          />
          {/* <div className="w-full border-1 border-black rounded mb-5"></div>
          <Delete onDelete={handleDelete} /> */}
        </div>
      </div>
    </>
  );
}
